import { Sparkles, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * Reusable module skeleton page for "Coming Soon" / "Basic Version" modules.
 * Used to make QrewHR feel enterprise-complete without over-engineering each module's backend.
 *
 * Props:
 *  - title, subtitle      → header copy
 *  - icon                 → lucide icon component
 *  - badge                → "Coming Soon" | "Basic Version" | "Beta"
 *  - tagline              → 1-line product positioning
 *  - features             → [{title, description, icon}]
 *  - capabilities         → string[] (planned capability bullets)
 *  - cta                  → optional { label, onClick }
 *  - children             → optional inline preview content
 *  - testid               → root data-testid
 */
export default function ModuleSkeleton({
  title, subtitle, icon: Icon = Sparkles,
  badge = "Coming Soon",
  tagline,
  features = [],
  capabilities = [],
  cta,
  children,
  testid = "module-skeleton",
}) {
  const badgeTone = {
    "Coming Soon":   "bg-slate-100 text-slate-700 border-slate-200",
    "Basic Version": "bg-amber-100 text-amber-800 border-amber-200",
    "Beta":          "bg-violet-100 text-violet-800 border-violet-200",
    "Preview":       "bg-cyan-100 text-cyan-800 border-cyan-200",
  }[badge] || "bg-slate-100 text-slate-700 border-slate-200";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid={testid}>
      {/* Hero — Qrew HR premium light theme */}
      <Card className="relative overflow-hidden border-slate-200 shadow-sm bg-white">
        {/* gradient brand strip */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-orange-400" />
        {/* soft decorative orbs */}
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-violet-200/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-fuchsia-200/30 blur-3xl pointer-events-none" />

        <div className="relative p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1 min-w-0 space-y-2.5">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${badgeTone} border font-bold uppercase tracking-wider text-[10px]`} data-testid="module-badge">
                {badge}
              </Badge>
              <span className="text-[11px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1">
                <span className="qh-grad-text">Qrew HR Module</span>
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold font-[Outfit] tracking-tight text-slate-900" data-testid="module-title">{title}</h1>
            {subtitle && <p className="text-slate-600 text-sm sm:text-base max-w-2xl leading-relaxed">{subtitle}</p>}
            {tagline && <p className="text-violet-700 text-xs uppercase tracking-[0.18em] font-bold pt-1">{tagline}</p>}
            {cta && (
              <div className="pt-3">
                <Button onClick={cta.onClick} className="qh-grad-bg text-white font-semibold shadow-md hover:shadow-lg" data-testid="module-cta">
                  {cta.label} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            )}
          </div>
          <div className="hidden lg:flex w-28 h-28 rounded-3xl bg-gradient-to-br from-violet-100 via-fuchsia-50 to-orange-50 ring-1 ring-violet-200 items-center justify-center shrink-0 shadow-sm">
            <Icon className="w-14 h-14 text-violet-600" strokeWidth={1.4} />
          </div>
        </div>
      </Card>

      {/* Inline content / preview */}
      {children}

      {/* Features grid */}
      {features.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <Card key={f.title} className="p-5 border-slate-200 shadow-sm bg-white hover:border-violet-300 hover:shadow-md transition group" data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-50 flex items-center justify-center mb-3 group-hover:scale-105 transition ring-1 ring-violet-100">
                {f.icon ? <f.icon className="w-5 h-5 text-violet-600" /> : <Sparkles className="w-5 h-5 text-violet-600" />}
              </div>
              <div className="font-bold text-slate-900 text-sm font-[Outfit]">{f.title}</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">{f.description}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Capability checklist */}
      {capabilities.length > 0 && (
        <Card className="p-5 sm:p-6 border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-violet-500" />
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Roadmap capabilities</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {capabilities.map((c) => (
              <div key={c} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-none mt-0.5" />
                <span>{c}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
